import { useRef, useCallback } from 'react';

interface SwipeHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

interface UseSwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
  enabled?: boolean;
}

/**
 * useSwipe — touch/pointer swipe detection for screen navigation.
 * Returns pointer event handlers to attach to the swipe target.
 */
export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  enabled = true,
}: UseSwipeOptions): SwipeHandlers {
  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (!enabled) return;
    startX.current = e.clientX;
    startY.current = e.clientY;
    tracking.current = true;
  }, [enabled]);

  const onPointerMove = useCallback((_e: React.PointerEvent) => {
    // Optional: could add visual drag feedback here
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!enabled || !tracking.current) return;
    tracking.current = false;

    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;

    // Only trigger if horizontal swipe is dominant
    if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy) * 1.2) {
      if (dx < 0) {
        onSwipeLeft?.();
      } else {
        onSwipeRight?.();
      }
    }
  }, [enabled, threshold, onSwipeLeft, onSwipeRight]);

  const onPointerCancel = useCallback(() => {
    tracking.current = false;
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}

export default useSwipe;
